import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { filter } from "lodash/fp";

import useDetectedLogin from "@app/analytics/hooks/sso/useDetectedLogin";
import * as Events from "@app/analytics/tracking/events";
import {
  getConnectedSSO,
  deleteConnectedSSO,
  ConnectedProvider,
  ConnectedCalendarProvider,
  ConnectedAccountProvider,
} from "@app/common/auth/api";
import useAuth from "@app/common/auth/useAuth";
import type { User } from "@app/common/types";

import {
  getAccountProvidersFromData,
  getCalendarProvidersFromData,
  isHighValueProvider,
} from "./ssoUtils";
import useAddAccountProvider from "./useAddAccountProvider";

const EMPTY_PROVIDERS: ConnectedProvider[] = [];

export const getQueryKey = (user: User | null) => ["ssoProviders", user?.id];

const useConnectedProviders = (
  {
    enabled = true,
    refetchInterval,
  }: {
    enabled?: boolean;
    refetchInterval?: number;
  } = { enabled: true }
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { addAccountProvider } = useAddAccountProvider();
  const { persistedLogin, setPersistedLogin } = useDetectedLogin();

  const queryKey = getQueryKey(user);

  const accountProvidersQuery = useQuery({
    queryKey,
    queryFn: getConnectedSSO,
    enabled: !!user && enabled,
    refetchInterval,
    staleTime: refetchInterval == null ? 5 * 60 * 10000 : undefined,
  });

  const deleteConnectedProviderMutation = useMutation({
    mutationFn: deleteConnectedSSO,
    onSuccess: (_data, providerId) => {
      if (persistedLogin?.method === providerId) {
        // if the provider they just removed was their most recent login method,
        // remove any knowledge of it
        setPersistedLogin(null);
      }
      Events.deleteProvider({ providerId });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["userCalendars", user!.id] });
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });

  let connectedAccountProviders: ConnectedAccountProvider[] = [];
  let connectedCalendarProviders: ConnectedCalendarProvider[] = [];
  let highValueCalendarProviders: ConnectedCalendarProvider[] = [];

  if (accountProvidersQuery.data) {
    connectedAccountProviders = getAccountProvidersFromData(
      accountProvidersQuery.data
    );

    connectedCalendarProviders = getCalendarProvidersFromData(
      accountProvidersQuery.data
    );

    highValueCalendarProviders = filter(
      isHighValueProvider,
      connectedCalendarProviders
    );
  }

  return {
    accountProvidersQuery,
    connectedProviders: accountProvidersQuery.data ?? EMPTY_PROVIDERS,
    isLoading: accountProvidersQuery.isLoading,
    connectedAccountProviders,
    /** @deprecated use integrations instead */
    connectedCalendarProviders,
    /** @deprecated use integrations instead */
    highValueCalendarProviders,
    addAccountProvider,
    deleteConnectedProvider: deleteConnectedProviderMutation,
    /** @deprecated use integrations instead */
    hasConnectedCalendarProviders: connectedCalendarProviders.length > 0,
    /** @deprecated use integrations instead */
    hasHighValueConnectedCalendarProviders:
      highValueCalendarProviders.length > 0,
  };
};

export default useConnectedProviders;
